import { Trans } from "@lingui/react/macro";
import { useLogout, useSession } from "@pontimed/auth-client";
import { Navigate, Outlet, createFileRoute, useMatches } from "@tanstack/react-router";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

import { AdminShell } from "@/components/layout/admin-shell";
import { AdminContextProvider } from "@/lib/admin-context";
import { hasAdminAccess, hasPermission } from "@/lib/permissions";
import { getPortalUrl } from "@/lib/portal-url";
import { permissionForRoute } from "@/lib/route-permissions";

/**
 * Auth + permission gate.
 *
 *   loading                         → skeleton
 *   unauthenticated                 → /login (preserve target via ?next=)
 *   authenticated, no admin access  → in-app 403 with "Sign out" + "Open portal"
 *                                     (defense in depth on top of Cloudflare Access IP allowlist)
 *   authenticated, admin access     → AdminShell
 *     - if active route requires a permission the user lacks → 403 panel
 *       (with the permission key shown so a superadmin can grant it)
 *     - else → render <Outlet />
 *
 * Source: `_docs/web-portal-navigation.md` D10. Replaces the previous binary
 * `isAdminRole` (isSuperadmin-only) gate with hasAdminAccess + per-route
 * hasPermission.
 */
export const Route = createFileRoute("/_authenticated")({
  component: AuthenticatedLayout,
});

function AuthenticatedLayout() {
  const { status, user } = useSession();
  const matches = useMatches();
  // Innermost match is the leaf route — that's the one whose permission gate applies.
  const activeRouteId = matches.length > 0 ? matches[matches.length - 1].routeId : undefined;
  const requiredPerm = permissionForRoute(activeRouteId);

  if (status === "loading") {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Skeleton className="h-12 w-48" />
      </div>
    );
  }

  if (status === "unauthenticated") {
    const next =
      typeof window !== "undefined" ? window.location.pathname + window.location.search : undefined;
    return <Navigate to="/login" search={next ? { next } : undefined} replace />;
  }

  if (!hasAdminAccess(user)) {
    return <ForbiddenPanel />;
  }

  // Per-route permission gate. When the leaf route is mapped to a specific
  // permission and the user lacks it, render an in-shell 403 instead of the
  // outlet so the sidebar (filtered by hasPermission) is still navigable.
  if (requiredPerm && !hasPermission(user, requiredPerm)) {
    return (
      <AdminContextProvider>
        <AdminShell>
          <MissingPermissionPanel permission={requiredPerm} />
        </AdminShell>
      </AdminContextProvider>
    );
  }

  return (
    <AdminContextProvider>
      <AdminShell>
        <Outlet />
      </AdminShell>
    </AdminContextProvider>
  );
}

function ForbiddenPanel() {
  const logout = useLogout();
  return (
    <div className="bg-background text-foreground flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
      <h1 className="text-2xl font-bold">
        <Trans>Staff access only</Trans>
      </h1>
      <p className="text-muted-foreground max-w-md text-sm">
        <Trans>Your account does not have admin permissions for this workspace.</Trans>
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Button
          variant="outline"
          onClick={() => {
            window.location.assign(getPortalUrl());
          }}
        >
          <Trans>Open portal</Trans>
        </Button>
        <Button
          onClick={() => {
            void logout.logout();
          }}
        >
          <Trans>Sign out</Trans>
        </Button>
      </div>
    </div>
  );
}

function MissingPermissionPanel({ permission }: { permission: string }) {
  return (
    <div className="bg-card border-border mx-auto mt-12 flex max-w-lg flex-col items-center gap-3 rounded-lg border px-6 py-10 text-center">
      <h2 className="text-foreground text-xl font-semibold">
        <Trans>403 — missing permission</Trans>
      </h2>
      <p className="text-muted-foreground text-sm">
        <Trans>
          Your account is missing the permission required to view this section. Ask a superadmin to
          grant it.
        </Trans>
      </p>
      <code className="bg-muted text-foreground rounded px-2 py-1 font-mono text-xs">
        {permission}
      </code>
    </div>
  );
}
